import { inject, Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { LoggingService } from '../logging.service';
import {
  DEVICE_COUNTERS_COLLECTION,

  DeviceCounterStateChange
} from './device-counter.models';

/** Seq `Category` shared by every event of this feature. */
export const DEVICE_COUNTER_SYNC_LOG_CATEGORY = 'DeviceCounterFirestoreSync';

/**
 * Structured Seq logging for device counter replica synchronization.
 *
 * Every replicated change gets exactly one terminal event — succeeded, failed
 * or skipped-as-stale — sharing the same property set, so a Seq query on
 * `Category = 'DeviceCounterFirestoreSync'` reconciles the two stores on its
 * own. A failure additionally carries the value the replica should have held.
 * No user data is logged beyond what {@link LoggingService} adds itself.
 */
@Injectable({ providedIn: 'root' })
export class DeviceCounterSyncLogger {
  private readonly loggingService = inject(LoggingService);

  /**
   * The counter reached Firestore: the backend acknowledged the write. Queued
   * writes are durable, so while the device is offline this lands on
   * reconnect — `DurationMs` (queued → acknowledged) shows the deferral.
   */
  logSyncSucceeded(change: DeviceCounterStateChange, created: boolean, queuedAt: Date): void {
    this.loggingService.info(
      'Device counter Firestore sync succeeded: {Operation} on counter {EntityType} of register {CashRegisterId} replicated after {NativeServiceOperation}',
      {
        ...this.describe(change),
        NativeServiceSucceeded: true,
        FirestoreSucceeded: true,
        FirestoreOutcome: created ? 'Created' : 'Updated',
        DurationMs: Date.now() - queuedAt.getTime()
      }
    );
  }

  /** The Native Service succeeded but the Firestore write did not (or could not be attempted). */
  logSyncFailure(change: DeviceCounterStateChange, error: unknown): void {
    const properties = {
      ...this.describe(change),
      NativeServiceSucceeded: true,
      FirestoreSucceeded: false,
      ExpectedState: { current: change.state?.current, lastResetAt: change.state?.lastResetAt?.toISOString?.() },
      ErrorCode: (error as any)?.code,
      ErrorMessage: (error as any)?.message ?? String(error),
      ErrorStack: (error as any)?.stack
    };

    this.loggingService.error(
      'Device counter Firestore sync failed: {Operation} on counter {EntityType} of register {CashRegisterId} after {NativeServiceOperation} succeeded',
      properties
    );
    console.error(
      `[DeviceCounterFirestoreSync] ${change.operation} on counter ${change.entityType} was not replicated to Firestore`,
      error
    );
    Sentry.captureException(error, {
      tags: { Category: DEVICE_COUNTER_SYNC_LOG_CATEGORY, Operation: change.operation },
      extra: { EntityType: change.entityType, CashRegisterId: change.cashRegisterId, CorrelationId: change.correlationId }
    });
  }

  /** The replica already held a newer value; recorded so a reconciliation can verify the ordering decision. */
  logStaleChangeSkipped(change: DeviceCounterStateChange, existingStamp: Date | undefined): void {
    this.loggingService.warning(
      'Device counter Firestore sync skipped a stale {Operation} on counter {EntityType} of register {CashRegisterId}',
      {
        ...this.describe(change),
        NativeServiceSucceeded: true,
        FirestoreSucceeded: true,
        FirestoreOutcome: 'SkippedStale',
        ExistingStamp: existingStamp?.toISOString()
      }
    );
  }

  private describe(change: DeviceCounterStateChange): Record<string, unknown> {
    const documentId = change.documentId || undefined;
    return {
      Category: DEVICE_COUNTER_SYNC_LOG_CATEGORY,
      Operation: change.operation,
      EntityType: change.entityType,
      CashRegisterId: change.cashRegisterId,
      CounterValue: change.state?.current,
      CorrelationId: change.correlationId,
      NativeServiceOperation: change.primaryOperation,
      FirestoreOperation: `set ${DEVICE_COUNTERS_COLLECTION}/${documentId}`,
      FirestoreCollection: DEVICE_COUNTERS_COLLECTION,
      FirestoreDocumentId: documentId,
      OccurredAt: change.occurredAt?.toISOString?.() ?? String(change.occurredAt),
      Online: typeof navigator !== 'undefined' ? navigator.onLine : undefined
    };
  }
}
